// Handoff between the ASPIRE app and the M2C2 assessment pages.
//
// ---------------------------------------------------------------------------
// WHY THE BATTERY IS NO LONGER AN IFRAME
// ---------------------------------------------------------------------------
// The battery used to run all 4 assessments in an iframe inside the SPA. On
// Android that froze the entire app partway through gameplay: every timer in
// the page stopped at once, with no error, no memory pressure (jsHeap 10MB of
// 1078MB) and a healthy GPU-backed CanvasKit surface. A touch would resume it.
// All timers dead + no error + recoverable by touch means the main thread was
// blocked waiting on the GPU, not crashed — and a same-origin iframe shares
// the parent's main thread, so the whole app went down with the game.
//
// The decisive evidence: the exact same assessment, opened as its own page on
// the same phone, plays through to completion without a single freeze. The
// assessment is fine; the engine is fine; the device is fine. Running it in an
// iframe is what breaks.
//
// So the battery now runs as real page navigations — the one configuration
// proven to work on the target hardware:
//
//   app  →  color-shapes  →  color-dots  →  grid-memory  →  symbol-search  →  app
//
// Each assessment is a top-level document with its own main thread. A GPU
// stall inside one can no longer take the app with it.
//
// The cost is that navigating away destroys everything held in memory —
// including the two audio recordings for this session. That is what this
// module exists to solve: the whole in-progress session is written to
// IndexedDB before the first navigation and restored when the battery
// returns. Nothing about the session is trusted to survive in memory.

import { addPendingUpload, getPendingUpload } from '../db/indexedDB';
import { logLine } from './m2c2Log';

// Single source of truth for the battery sequence. The assessment pages have
// their own copy of this list baked in (they have to — they do the chaining
// themselves), so if you change it here, regenerate the bundle pages too.
export const ACTIVITIES = [
  { id: 'color-shapes', pkg: '@m2c2kit/assessment-color-shapes', version: '0.8.35' },
  { id: 'color-dots', pkg: '@m2c2kit/assessment-color-dots', version: '0.8.35' },
  { id: 'grid-memory', pkg: '@m2c2kit/assessment-grid-memory', version: '0.8.35' },
  { id: 'symbol-search', pkg: '@m2c2kit/assessment-symbol-search', version: '0.8.35' },
];

// Shared with the assessment pages. Kept in localStorage rather than
// IndexedDB because the assessment pages are plain scripts with no bundler
// and no async setup — a synchronous read/write there is worth far more than
// the extra headroom, and trial data for four assessments is well under any
// realistic quota.
const RESULTS_KEY = 'aspire_m2c2_results_v1';

// Marks the session record that is parked mid-session. Deliberately NOT
// 'pending' or 'failed', which are the two statuses retryPendingUploads()
// picks up — a session that hasn't finished must never be swept into the
// upload queue.
const IN_SESSION_STATUS = 'in_session';

export function activityUrl({ pkg, version }, extraParams = '') {
  return `/m2c2-bundle/assessments/${pkg}@${version}/index.html?m2c2=run${extraParams}`;
}

// Explicit whitelist of the session store's DATA fields.
//
// This has to be a whitelist, not a spread: sessionStore.getSnapshot() returns
// the entire store, which includes its action functions, and IndexedDB uses
// structured clone — functions throw DataCloneError. Listing the fields also
// means adding a new one to the store without adding it here fails visibly in
// testing rather than silently dropping data from the manifest.
const SESSION_FIELDS = [
  'sessionId',
  'participantId',
  'sessionNumber',
  'contentIndex',
  'dayNumber',
  'sessionWithinDay',
  'startTsUtc',
  'affectPre',
  'affectPost',
  'textTrial',
  'imageTrial',
  'deviceInfo',
  'm2c2Results',
  'abandoned',
  'abandonmentPhase',
];

function dataOnly(snapshot) {
  const out = {};
  for (const k of SESSION_FIELDS) out[k] = snapshot[k] ?? null;
  return out;
}

/**
 * Park the whole in-progress session in IndexedDB so it survives the
 * navigations. Audio Blobs are stored directly — IndexedDB handles them
 * natively, which is the reason this isn't localStorage.
 */
export async function parkSession(rawSnapshot) {
  const snapshot = dataOnly(rawSnapshot);
  const record = {
    session_id: snapshot.sessionId,
    participant_id: snapshot.participantId,
    created_at: new Date().toISOString(),
    status: IN_SESSION_STATUS,
    attempt_count: 0,
    last_attempt_at: null,
    manifest: null,
    // The scratch session state, minus the Blobs, which are stored alongside
    // so the record shape matches what SessionCompleteScreen already writes.
    parked_session: {
      ...snapshot,
      textTrial: snapshot.textTrial ? { ...snapshot.textTrial, blob: undefined } : null,
      imageTrial: snapshot.imageTrial ? { ...snapshot.imageTrial, blob: undefined } : null,
    },
    text_audio_blob: snapshot.textTrial?.blob ?? null,
    image_audio_blob: snapshot.imageTrial?.blob ?? null,
  };
  await addPendingUpload(record);
  logLine(`session ${snapshot.sessionId} parked for battery handoff`);
}

/** Restore a parked session, reattaching the audio Blobs to their trials. */
export async function unparkSession(sessionId) {
  const record = await getPendingUpload(sessionId);
  if (!record?.parked_session) return null;
  const s = record.parked_session;
  return {
    ...s,
    textTrial: s.textTrial ? { ...s.textTrial, blob: record.text_audio_blob ?? undefined } : null,
    imageTrial: s.imageTrial ? { ...s.imageTrial, blob: record.image_audio_blob ?? undefined } : null,
  };
}

/**
 * Open a fresh results accumulator and record which session it belongs to.
 * The session id is stored with it so a stale accumulator from an abandoned
 * run can never be mistaken for this one's data.
 */
export function startBattery(sessionId) {
  const state = {
    sessionId,
    battery_start_ts_utc: new Date().toISOString(),
    activities: {},
  };
  try {
    localStorage.setItem(RESULTS_KEY, JSON.stringify(state));
  } catch (e) {
    logLine(`WARN: could not open results accumulator — ${String(e)}`);
  }
  return state;
}

/**
 * Read back what the assessments collected. Returns null if there's nothing
 * for this session, so the caller can tell "battery produced no data" apart
 * from "leftovers from some other run".
 */
export function readBatteryResults(sessionId) {
  let state;
  try {
    const raw = localStorage.getItem(RESULTS_KEY);
    state = raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
  if (!state || state.sessionId !== sessionId) return null;

  const activities = {};
  let completed = 0;
  for (const a of ACTIVITIES) {
    const got = state.activities?.[a.id];
    if (got) completed += 1;
    activities[a.id] = {
      activity_id: a.id,
      activity_version: a.version,
      trials: got?.trials ?? [],
    };
  }

  return {
    battery_complete: completed === ACTIVITIES.length,
    battery_start_ts_utc: state.battery_start_ts_utc,
    battery_end_ts_utc: new Date().toISOString(),
    activities,
  };
}

export function clearBatteryResults() {
  try {
    localStorage.removeItem(RESULTS_KEY);
  } catch (e) { /* non-fatal */ }
}

/**
 * Park the session, open the accumulator, and hand off to the first
 * assessment. Uses assign() rather than replace() so the app remains in the
 * history stack behind the battery.
 */
export async function beginBattery(snapshot) {
  await parkSession(snapshot);
  startBattery(snapshot.sessionId);
  const url = activityUrl(ACTIVITIES[0]);
  logLine(`handing off to ${ACTIVITIES[0].id} → ${url}`);
  window.location.assign(url);
}
